import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AppIcon from './AppIcon';

interface RootState {
  user: { role: string; isManager: boolean };
}

interface RoleProtectedRouteProps {
  allowedRoles: string[];
  children: React.ReactElement;
}

// How long the "no access" notice stays on screen before the guard redirects home.
const REDIRECT_DELAY_MS = 2200;

/**
 * Route guard that restricts access by the current user's role.
 *
 * Effective roles mirror `useFilteredNavigation`: the user's primary `role`
 * string plus the synthetic `'Manager'` role when `isManager` is set (the user
 * has direct reports). This keeps route access consistent with sidebar
 * visibility — otherwise a people-manager whose primary role is `'Clocking'`
 * would see manager menu items but get bounced when clicking them.
 *
 * When a blocked role reaches a restricted route directly (typed/bookmarked URL),
 * the redirect is no longer silent: a brief "you don't have access" notice is
 * shown before bouncing to the dashboard, so the bounce isn't confusing.
 */
export const RoleProtectedRoute = ({ allowedRoles, children }: RoleProtectedRouteProps) => {
  const { t } = useTranslation('common');
  const role = useSelector((state: RootState) => state.user.role);
  const isManager = useSelector((state: RootState) => state.user.isManager);

  const effectiveRoles = new Set([role]);
  if (isManager) effectiveRoles.add('Manager');

  const allowed = allowedRoles.some(r => effectiveRoles.has(r));

  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    if (allowed) return;
    const timer = setTimeout(() => setRedirect(true), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [allowed]);

  if (allowed) return children;
  if (redirect) return <Navigate to="/" replace />;

  return (
    <div role="alert" aria-live="assertive" className="flex flex-1 items-center justify-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AppIcon name="ShieldAlert" size={24} />
        </div>
        <h2 className="text-base font-semibold text-foreground">
          {t("You don't have access to this page")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('Redirecting you to your dashboard…')}
        </p>
        <button
          type="button"
          onClick={() => setRedirect(true)}
          className="mt-1 text-sm font-medium text-primary hover:underline"
        >
          {t('Go to dashboard')}
        </button>
      </div>
    </div>
  );
};
